// @flow
import React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

type Props = {
  children: React.Node,
  hasSubheader?: boolean, // true = toolbar height (header + subheader), false = toolbarHalf height (header only)
  sx?: Object
};

/**
 * ContentContainer - Wrapper component that ensures content is positioned below the fixed header
 *
 * This component handles the padding needed to push content below the AppBar which is position: fixed.
 * Use this as a wrapper for all main content components (TenantsContent, MemberContent, etc.)
 *
 * @param {React.Node} children - The content to render
 * @param {boolean} hasSubheader - Whether the page has a subheader (default: true)
 *   - true: Uses toolbar height (header + subheader + padding = 172px desktop, 156px mobile)
 *   - false: Uses toolbarHalf height (header only + padding = 102px desktop, 86px mobile)
 * @param {Object} sx - Additional MUI sx styles to apply
 *
 * @example
 * // For pages with subheader (Customers, Members, etc.)
 * <ContentContainer hasSubheader={true} sx={customStyles}>
 *   <YourContent />
 * </ContentContainer>
 *
 * @example
 * // For pages without subheader
 * <ContentContainer hasSubheader={false}>
 *   <YourContent />
 * </ContentContainer>
 */
const ContentContainer = ({ children, hasSubheader = true, sx = {}, ...props }: Props) => {
  const theme = useTheme();

  // Get header height from theme
  const headerHeight = theme.layout.customProps.header.height; // 70px
  const desktopContentPadding = 32; // Spacing between header and content on desktop
  const mobileContentPadding = 16; // theme.spacing(2) - mobile-appropriate spacing

  // Desktop: header height(s) + content padding
  // USE STRING WITH PX UNIT (MUI sx prop interprets numbers as spacing multipliers)
  const desktopPaddingTop = hasSubheader
    ? `${headerHeight * 2 + desktopContentPadding}px`   // Header + subheader + padding: "172px"
    : `${headerHeight + desktopContentPadding}px`;       // Header only + padding: "102px"

  // Mobile: header height(s) + mobile padding
  const mobilePaddingTop = hasSubheader
    ? `${headerHeight * 2 + mobileContentPadding}px`  // Header + subheader + padding: "156px"
    : `${headerHeight + mobileContentPadding}px`;      // Header only + padding: "86px"

  return (
    <Box
      sx={{
        ...sx, // Spread custom styles FIRST
        flexGrow: 1, // From theme.layout.content
        paddingTop: desktopPaddingTop, // Desktop spacing
        [theme.breakpoints.down('sm')]: {
          // Mobile spacing (overrides desktop)
          paddingTop: mobilePaddingTop,
          paddingLeft: '12px',
          paddingRight: '12px'
        }
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export default ContentContainer;
